import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiChevronDown } from 'react-icons/fi';

const Hero = () => {
  const { scrollY } = useScroll();

  // Parallax effects
  const backgroundY = useTransform(scrollY, [0, 1000], [0, -300]);
  const textY = useTransform(scrollY, [0, 1000], [0, -150]);

  const scrollToNext = () => {
    window.scrollBy({ top: window.innerHeight, behavior: 'smooth' });
  };

  return (
    <section className="relative h-screen flex items-center justify-center overflow-hidden">
      {/* Parallax Background */}
      <motion.div 
        className="absolute inset-0 transform scale-110"
        style={{ y: backgroundY }}
      >
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.1), rgba(23, 33, 74, 0.7)), url('https://images.unsplash.com/photo-1598525315998-cb8c3146aecd?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D')`
          }}
        />
      </motion.div>

      {/* Subtle grid overlay */}
      <motion.div 
        className="absolute inset-0 opacity-5"
        style={{ y: textY }}
      >
        <div
          className="w-full h-full bg-gradient-to-br from-aero-blue/10 to-transparent"
          style={{
            backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(159, 203, 220, 0.05) 0%, transparent 50%)'
          }}
        />
      </motion.div>

      {/* Hero Content */}
      <motion.div
        className="relative z-10 text-center text-white max-w-6xl mx-auto px-6 mt-16"
        style={{ y: textY }}
      >
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="space-y-8"
        >
          {/* Bold Hero Text */}
          <motion.h1
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold leading-[0.85] tracking-tight"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <motion.span
              className="block font-bold"
              animate={{
                textShadow: [
                  '0 0 0px rgba(255, 255, 255, 0)',
                  '0 0 30px rgba(255, 255, 255, 0.3)',
                  '0 0 0px rgba(255, 255, 255, 0)'
                ]
              }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              Precision
            </motion.span>
            <span className="bg-gradient-to-r from-white via-aero-blue to-cyan-300 bg-clip-text text-transparent font-bold">
              Reliability
            </span>
            <span className="block font-bold">Safety</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            className="text-lg md:text-xl lg:text-2xl text-white/90 max-w-4xl mx-auto leading-relaxed font-light"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            Your trusted partner for aviation asset management, continuing airworthiness, 
            and comprehensive maintenance solutions across the globe.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center mt-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <Link to="/contact">
              <motion.button
                className="shiny-button bg-gradient-to-r from-aero-blue to-cyan-400 text-white px-8 py-3 rounded-2xl font-semibold text-lg transition-all duration-500 shadow-2xl relative overflow-hidden"
                whileHover={{
                  scale: 1.05,
                  y: -2,
                  boxShadow: '0 25px 50px rgba(159, 203, 220, 0.4)'
                }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="relative z-10">Get Started</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 transform -skew-x-12 -translate-x-full transition-transform duration-700 hover:translate-x-full hover:opacity-40"></div>
              </motion.button>
            </Link>

            <motion.button
              onClick={scrollToNext}
              className="shiny-button border-2 border-white/70 text-white px-8 py-3 rounded-2xl font-semibold text-lg transition-all duration-500 backdrop-blur-sm relative overflow-hidden"
              whileHover={{
                scale: 1.05,
                y: -2,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderColor: 'rgba(255, 255, 255, 0.9)'
              }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="relative z-10">Learn More</span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 transform -skew-x-12 -translate-x-full transition-transform duration-700 hover:translate-x-full hover:opacity-20"></div>
            </motion.button>
          </motion.div>

          {/* Stats Section */}
          <motion.div
            className="grid grid-cols-2 md:grid-cols-5 gap-6 mt-16 pt-8 border-t border-white/20 items-center"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
          >
            {[
              { value: '2024', label: 'Founded' },
              { value: '24/7', label: 'Support' }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                className="text-center"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 1.3 + index * 0.1 }}
                whileHover={{ scale: 1.05, y: -2 }}
              >
                <div className="text-3xl md:text-4xl font-light text-white mb-2">{stat.value}</div>
                <div className="text-sm text-white/70 font-light tracking-wide uppercase">{stat.label}</div>
              </motion.div>
            ))}

            <motion.div
              className="text-center col-span-2 md:col-span-1"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 1.5 }}
            >
              <motion.div
                className="cursor-pointer inline-flex flex-col items-center"
                onClick={scrollToNext}
                animate={{ y: [0, 6, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                whileHover={{ scale: 1.1 }}
              >
                <span className="text-xs mb-3 font-light tracking-wider uppercase text-white/80 hover:text-white transition-colors duration-300">
                  Discover More
                </span>
                <div className="p-2 border border-white/40 rounded-full backdrop-blur-sm hover:bg-white/10 hover:border-white/60 transition-all duration-300">
                  <FiChevronDown size={14} className="text-white/80 hover:text-white transition-colors duration-300" />
                </div>
              </motion.div>
            </motion.div>

            {[
              { value: 'Global', label: 'Reach' },
              { value: 'NZ', label: 'Based' }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                className="text-center"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 1.6 + index * 0.1 }}
                whileHover={{ scale: 1.05, y: -2 }}
              >
                <div className="text-3xl md:text-4xl font-light text-white mb-2">{stat.value}</div>
                <div className="text-sm text-white/70 font-light tracking-wide uppercase">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
